/**
 * ConsentChecklist — accordion list of consent items shown during
 * signup and on the consent-management settings page.
 *
 * Each row stays collapsed showing the title + short summary; tapping
 * it reveals the full text. The accept checkbox is locked until the
 * user has expanded the row at least once — that's the "informed
 * consent" guard the DPDP Act effectively requires.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { Card, CardBody, Collapse, Spinner, Alert, Button } from 'reactstrap';
import { FiCheck, FiChevronDown, FiChevronUp, FiAlertCircle } from 'react-icons/fi';

const styles = {
  // Separate `borderWidth/Style/Color` longhand props instead of the
  // `border` shorthand — when we later override only `borderColor`
  // for the active state, React warns about mixing shorthand +
  // longhand on the same value (and the override silently no-ops in
  // some browsers). Longhand keeps the diff predictable.
  card: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#e2e8f0',
    borderRadius: 12,
    marginBottom: 12,
    overflow: 'hidden',
  },
  cardActive: {
    borderColor: '#7C3AED',
    boxShadow: '0 0 0 3px rgba(26, 115, 232, 0.12)',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: 16,
    cursor: 'pointer',
    background: '#fff',
  },
  headerText: { flex: 1, minWidth: 0 },
  title: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: '#0f172a',
    margin: 0,
    marginBottom: 4,
  },
  required: {
    color: '#7C3AED',
    fontSize: '0.75rem',
    fontWeight: 700,
    marginLeft: 8,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  summary: {
    fontSize: '0.85rem',
    color: '#475569',
    lineHeight: 1.5,
    margin: 0,
  },
  body: {
    padding: 16,
    background: '#f8fafc',
    borderTop: '1px solid #e2e8f0',
    fontSize: '0.85rem',
    color: '#334155',
    lineHeight: 1.6,
  },
  version: {
    fontSize: '0.75rem',
    color: '#94a3b8',
    fontStyle: 'italic',
    marginTop: 8,
  },
  checkRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 16px',
    borderTop: '1px solid #e2e8f0',
    background: '#fff',
    cursor: 'pointer',
    userSelect: 'none',
  },
  checkRowDisabled: { opacity: 0.55, cursor: 'not-allowed' },
  checkBox: {
    width: 22,
    height: 22,
    borderRadius: 6,
    // Longhand — `cardActive`/`checkBoxDisabled` override the colour
    // alone, so the longhand props avoid the React shorthand warning.
    borderWidth: 1.5,
    borderStyle: 'solid',
    borderColor: '#94a3b8',
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  checkBoxOn: {
    background: '#7C3AED',
    borderColor: '#7C3AED',
    color: '#fff',
  },
  checkBoxDisabled: {
    background: '#f1f5f9',
    borderColor: '#cbd5e1',
  },
  checkLabel: { fontSize: '0.85rem', color: '#334155', flex: 1 },
  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 4px',
    fontSize: '0.85rem',
    color: '#475569',
  },
};

/**
 * @param {{
 *   items: Array<{ code: string, version: string, title: string, summary: string, mandatory: boolean }>,
 *   acceptance: Record<string, { read: boolean, accepted: boolean }>,
 *   onChange: (next: Record<string, { read: boolean, accepted: boolean }>) => void,
 * }} props
 */
const ConsentChecklist = ({ items, acceptance, onChange }) => {
  const update = useCallback(
    (code, patch) => {
      const current = acceptance[code] || { read: false, accepted: false };
      onChange({ ...acceptance, [code]: { ...current, ...patch } });
    },
    [acceptance, onChange],
  );

  const allMandatoryAccepted = useMemo(
    () => items.filter((i) => i.mandatory).every((i) => acceptance[i.code]?.accepted),
    [items, acceptance],
  );

  return (
    <div>
      {items.map((item) => (
        <ConsentRow
          key={item.code}
          item={item}
          state={acceptance[item.code] || { read: false, accepted: false }}
          onChange={(patch) => update(item.code, patch)}
        />
      ))}

      <div style={styles.footer}>
        {allMandatoryAccepted ? (
          <>
            <FiCheck size={16} color="#10b981" />
            <span>
              Required consents accepted. The optional ones can be changed
              any time from Settings → Privacy.
            </span>
          </>
        ) : (
          <>
            <FiAlertCircle size={16} color="#94a3b8" />
            <span>Open every required item to read in full before continuing.</span>
          </>
        )}
      </div>
    </div>
  );
};

const ConsentRow = ({ item, state, onChange }) => {
  const [expanded, setExpanded] = useState(false);

  const toggleExpand = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !state.read) onChange({ read: true });
  };

  const toggleAccept = () => {
    if (!state.read) return;
    onChange({ accepted: !state.accepted });
  };

  const checkboxDisabled = !state.read;

  return (
    <Card style={{ ...styles.card, ...(state.accepted ? styles.cardActive : null) }}>
      <div style={styles.header} onClick={toggleExpand} role="button" tabIndex={0}
           onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleExpand(); }}>
        <div style={styles.headerText}>
          <h6 style={styles.title}>
            {item.title}
            {item.mandatory ? <span style={styles.required}>· Required</span> : null}
          </h6>
          <p style={styles.summary}>{item.summary}</p>
        </div>
        {expanded ? <FiChevronUp size={18} color="#64748b" /> : <FiChevronDown size={18} color="#64748b" />}
      </div>

      <Collapse isOpen={expanded}>
        <div style={styles.body}>
          {item.summary}
          <div style={styles.version}>Version {item.version}</div>
        </div>
      </Collapse>

      <div
        style={{ ...styles.checkRow, ...(checkboxDisabled ? styles.checkRowDisabled : null) }}
        onClick={toggleAccept}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleAccept(); }}
      >
        <span
          style={{
            ...styles.checkBox,
            ...(state.accepted ? styles.checkBoxOn : null),
            ...(checkboxDisabled ? styles.checkBoxDisabled : null),
          }}
        >
          {state.accepted ? <FiCheck size={14} /> : null}
        </span>
        <span style={styles.checkLabel}>
          {checkboxDisabled
            ? 'Tap above to read in full, then accept.'
            : `I have read and ${item.mandatory ? 'agree to the' : 'accept the'} ${item.title}.`}
        </span>
      </div>
    </Card>
  );
};

/** True iff every mandatory consent in `items` is accepted. */
export const allMandatoryAccepted = (items, acceptance) =>
  items.filter((i) => i.mandatory).every((i) => acceptance[i.code]?.accepted);

/** Loading + error wrapper used at signup time when the catalogue is fetched fresh. */
export const ConsentCatalogueView = ({ items, acceptance, onChange, loading, error, onRetry }) => {
  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner size="sm" /> <span className="ms-2 text-muted">Loading consent catalogue…</span>
      </div>
    );
  }
  if (error || !items || items.length === 0) {
    return (
      <Alert fade={false} color="warning" className="d-flex align-items-center justify-content-between">
        <span>We couldn't load the consent catalogue. Please try again.</span>
        <Button size="sm" color="warning" outline onClick={onRetry}>Retry</Button>
      </Alert>
    );
  }
  return <ConsentChecklist items={items} acceptance={acceptance} onChange={onChange} />;
};

export default ConsentChecklist;
